import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CryptoJS from "crypto-js";
import Request from "../../../helpers/request.service";
import { authUrl } from "../../../constants/apiBaseUrl";
import { useAuth } from "../../../lib/auth";
import AcceptInviteForm from "./AcceptInviteForm";

/**
 * AcceptInvite — public landing page for the single-use invite link.
 *
 * Reads ?token=... from the URL, accepts the password the user types,
 * SHA-512 hashes it (matching the rest of the auth flows), POSTs to
 * /auth/accept-invite, then hands the returned access+refresh pair to
 * useAuth() and lands the user on /.
 */
export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { loginWithTokens } = useAuth();

  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (!token) setErrorMessage("This invite link is invalid.");
  }, [token]);

  const handleSubmit = async (password) => {
    if (!token) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const passwordHash = CryptoJS.SHA512(password).toString(CryptoJS.enc.Hex);
      const res = await Request.postJson(authUrl.accept_invite, { token, password: passwordHash });
      const payload = res?.data?.data;
      if (!payload?.accessToken || !payload?.refreshToken) {
        setErrorMessage(res?.data?.message || "Unexpected response from server.");
        return;
      }
      loginWithTokens({
        accessToken: payload.accessToken,
        refreshToken: payload.refreshToken,
        user: {
          id: payload.userId,
          email: payload.email,
          name: payload.fullName,
          role: payload.roleName,
          roleId: payload.roleId,
        },
      });
      navigate("/", { replace: true });
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Could not accept the invite. The link may have expired.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4">
      <div className="w-full max-w-[460px] bg-milk border border-border rounded-2xl p-8">
        <div className="text-center">
          <div className="font-display text-4xl text-cocoa">
            <span className="text-honey">✿</span> Sulsa <span className="text-honey">✿</span>
          </div>
          <div className="text-honey italic text-sm mt-1">Welcome aboard</div>
        </div>
        <h1 className="mt-6 font-display text-2xl text-cocoa text-center">Set your password</h1>
        <p className="mt-2 text-sm text-mahogany text-center">
          Choose a password to activate your account. The invite link is single-use.
        </p>
        {!token ? (
          <p className="mt-6 text-sm text-red-600 text-center">
            This invite link is invalid. Please ask your administrator to resend the invite.
          </p>
        ) : (
          <AcceptInviteForm
            submitting={submitting}
            errorMessage={errorMessage}
            onSubmit={handleSubmit}
          />
        )}
      </div>
    </div>
  );
}
